'use client';

import React, { useState } from 'react';
import { X, MapPin, Phone, Mail, Truck, CheckCircle2, Circle, Clock, MessageSquare, Download, AlertTriangle, RotateCcw, Loader2,  } from 'lucide-react';
import type { Order } from './OrderManagementContent';
import AppImage from '@/components/ui/AppImage';

interface OrderDetailPanelProps {
  order: Order;
  onClose: () => void;
}

export default function OrderDetailPanel({ order, onClose }: OrderDetailPanelProps) {
  const [trackingInput, setTrackingInput] = useState(order.trackingNumber ?? '');
  const [savingTracking, setSavingTracking] = useState(false);
  const [trackingSaved, setTrackingSaved] = useState(false);
  const [acceptingOrder, setAcceptingOrder] = useState(false);

  // Backend integration: PATCH /api/seller/orders/:id/tracking { tracking_number: string }
  const saveTracking = async () => {
    if (!trackingInput.trim()) return;
    setSavingTracking(true);
    await new Promise((r) => setTimeout(r, 900));
    setSavingTracking(false);
    setTrackingSaved(true);
    setTimeout(() => setTrackingSaved(false), 2000);
  };

  // Backend integration: PATCH /api/seller/orders/:id/accept
  const acceptOrder = async () => {
    setAcceptingOrder(true);
    await new Promise((r) => setTimeout(r, 1000));
    setAcceptingOrder(false);
  };

  const statusColorMap: Record<string, string> = {
    pending: 'text-warning border-warning/30 bg-warning/10',
    processing: 'text-info border-info/30 bg-info/10',
    shipped: 'text-purple-700 border-purple-200 bg-purple-50',
    delivered: 'text-success border-success/30 bg-success/10',
    cancelled: 'text-danger border-danger/30 bg-danger/10',
    return: 'text-orange-700 border-orange-200 bg-orange-50',
    dispute: 'text-red-800 border-red-300 bg-red-50',
  };

  return (
    <div className="card slide-in-right overflow-hidden flex flex-col max-h-[calc(100vh-140px)] sticky top-20">
      {/* Header */}
      <div className="flex items-start justify-between px-5 py-4 border-b border-border shrink-0">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <code className="text-sm font-mono font-bold text-primary">{order.id}</code>
            {order.fba && (
              <span className="text-2xs bg-primary text-white px-1.5 py-0.5 rounded font-bold">FBA</span>
            )}
          </div>
          <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${statusColorMap[order.status]}`}>
            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
          </div>
        </div>
        <button onClick={onClose} className="btn-ghost p-1.5 shrink-0">
          <X size={16} />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {/* Action bar for actionable statuses */}
        {order.status === 'pending' && (
          <div className="px-5 py-3 bg-warning/10 border-b border-warning/20 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-warning font-medium">
              <Clock size={13} />
              Accept within 12h or auto-cancelled
            </div>
            <button
              onClick={acceptOrder}
              disabled={acceptingOrder}
              className="btn-primary text-xs py-1.5 px-3 min-w-[90px] justify-center"
            >
              {acceptingOrder ? <Loader2 size={12} className="animate-spin" /> : 'Accept Order'}
            </button>
          </div>
        )}

        {order.status === 'dispute' && (
          <div className="px-5 py-3 bg-danger/10 border-b border-danger/20 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-danger font-medium">
              <AlertTriangle size={13} />
              Respond to dispute — due 10 May 2026
            </div>
            <button className="btn-danger text-xs py-1.5 px-3">Respond Now</button>
          </div>
        )}

        <div className="px-5 py-4 space-y-5">
          {/* Customer info */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Customer</p>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/40">
              <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold shrink-0">
                {order.customer.split(' ').map((n) => n[0]).join('').slice(0, 2)}
              </div>
              <div className="space-y-1 text-xs">
                <p className="font-semibold text-foreground">{order.customer}</p>
                <p className="flex items-center gap-1.5 text-muted-foreground">
                  <MapPin size={11} /> {order.shippingAddress}
                </p>
                <p className="flex items-center gap-1.5 text-muted-foreground">
                  <Phone size={11} /> {order.customerPhone}
                </p>
                <p className="flex items-center gap-1.5 text-muted-foreground">
                  <Mail size={11} /> {order.customerEmail}
                </p>
              </div>
            </div>
          </div>

          {/* Order items */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Order Items ({order.productCount})
            </p>
            <div className="space-y-2">
              {order.items.map((item, idx) => (
                <div
                  key={`item-${order.id}-${idx}`}
                  className="flex items-center gap-3 p-2.5 rounded-lg border border-border"
                >
                  <AppImage
                    src={item.image}
                    alt={item.imageAlt}
                    width={40}
                    height={40}
                    className="w-10 h-10 rounded-lg object-cover border border-border shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground leading-snug line-clamp-2">{item.name}</p>
                    <code className="text-2xs text-muted-foreground font-mono">{item.sku}</code>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-bold tabular-nums">PKR {item.price.toLocaleString('en-PK')}</p>
                    <p className="text-2xs text-muted-foreground">Qty: {item.qty}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order summary */}
          <div className="p-3 rounded-lg bg-muted/40 space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-medium tabular-nums">PKR {order.amount.toLocaleString('en-PK')}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Shipping</span>
              <span className="font-medium text-success">Free</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Platform Fee (8%)</span>
              <span className="font-medium text-danger tabular-nums">
                - PKR {Math.round(order.amount * 0.08).toLocaleString('en-PK')}
              </span>
            </div>
            <div className="flex justify-between text-xs pt-1.5 border-t border-border">
              <span className="font-semibold">Net Earnings</span>
              <span className="font-bold text-success tabular-nums">
                PKR {Math.round(order.amount * 0.92).toLocaleString('en-PK')}
              </span>
            </div>
          </div>

          {/* Tracking */}
          {(order.status === 'processing' || order.status === 'shipped') && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Tracking Number
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={trackingInput}
                  onChange={(e) => setTrackingInput(e.target.value)}
                  placeholder="e.g. TCS-PKR-20260510-88421"
                  className="input-field text-xs h-9 flex-1 font-mono"
                />
                <button
                  onClick={saveTracking}
                  disabled={savingTracking || !trackingInput.trim()}
                  className="btn-primary text-xs px-3 h-9 min-w-[70px] justify-center"
                >
                  {savingTracking ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : trackingSaved ? (
                    '✓ Saved'
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
              <p className="text-2xs text-muted-foreground mt-1">
                Adding tracking marks order as Shipped and notifies customer
              </p>
            </div>
          )}

          {order.trackingNumber && order.status !== 'processing' && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Tracking</p>
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/40">
                <Truck size={14} className="text-primary shrink-0" />
                <code className="text-xs font-mono font-semibold">{order.trackingNumber}</code>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Order Timeline</p>
            <div className="space-y-0">
              {order.timeline.map((event, idx) => (
                <div key={`timeline-${order.id}-${idx}`} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    {event.done ? (
                      <CheckCircle2 size={16} className="text-success shrink-0" />
                    ) : (
                      <Circle size={16} className="text-border shrink-0" />
                    )}
                    {idx < order.timeline.length - 1 && (
                      <div className={`w-px flex-1 my-0.5 ${event.done ? 'bg-success/40' : 'bg-border'}`} />
                    )}
                  </div>
                  <div className="pb-3 flex-1">
                    <p className={`text-xs font-semibold ${event.done ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {event.event}
                    </p>
                    <p className="text-2xs text-muted-foreground">{event.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div className="shrink-0 border-t border-border px-5 py-3 flex items-center gap-2 bg-muted/20">
        <button className="btn-ghost text-xs flex-1 justify-center">
          <MessageSquare size={13} />
          Message
        </button>
        <button className="btn-ghost text-xs flex-1 justify-center">
          <Download size={13} />
          Invoice
        </button>
        {order.status === 'delivered' && (
          <button className="btn-ghost text-xs flex-1 justify-center text-orange-600 hover:text-orange-700">
            <RotateCcw size={13} />
            Refund
          </button>
        )}
      </div>
    </div>
  );
}